/**
 * 게임 템플릿(텍스트 프리셋) — 결정 #35. 제작 사다리의 최저 진입장벽. 장르 템플릿을
 * 고르고 빈칸 몇 개만 채우면 완성된 GameDefinition(결정 #29)이 나온다. 결과는
 * 그대로 GameCard로 저장되어 상위 모드(폼 에디터·코드)에서 더 다듬을 수 있다(상향 호환).
 *
 * 플랫폼 중립(결정 #10): 템플릿은 필드 선언 + 순수 빌더 함수다. 빌더는 채운 값으로
 * GameDefinition을 합성하기만 한다(네트워크·DOM 없음). 큐레이션 템플릿은 코드 정의
 * (우리 제공). 채운 결과물은 데이터(GameCard)라 공유 가능하다(#29 카드 IO).
 */
import type { GameDefinition } from "./game-definition.js";

/** 템플릿이 사용자에게 채워달라고 하는 빈칸 하나(결정 #35). */
export interface GameTemplateField {
  /** 값을 식별하는 키(빌더에 전달). */
  key: string;
  /** UI 라벨. */
  label: string;
  /** 입력 힌트(placeholder). */
  placeholder?: string;
  /** 비우면 쓰일 기본값(예시). */
  default?: string;
  /** 여러 줄 입력인지(설명 등). */
  multiline?: boolean;
}

/**
 * 한 장르 템플릿(결정 #35). fields로 빈칸을 선언하고, build로 채운 값을 받아
 * 완성된 GameDefinition을 만든다. id는 빈 문자열로 두고 카드 팩토리가 재시드한다.
 */
export type Translate = (k: string, params?: Record<string, string|number>) => string;

export interface GameTemplate {
  id: string;
  title: string;
  description: string;
  icon?: string;
  fields: GameTemplateField[];
  /** Optional human-authored .rqt source to keep in .rqp packages. */
  source?(values: Record<string, string>): string;
  /** 채운 값 → GameDefinition(순수). 누락 값은 buildFromTemplate가 기본값으로 채워 전달. */
  build(values: Record<string, string>, t?: Translate): GameDefinition;
}

/**
 * 템플릿과 사용자 입력으로 GameDefinition을 만든다(결정 #35). 누락/공백 필드는
 * 템플릿의 기본값으로 채운 뒤 빌더에 넘겨, 빈칸을 안 채워도 동작하는 게임이 나온다.
 */
export function buildFromTemplate(
  template: GameTemplate,
  values: Record<string, string>,
  t?: Translate
): GameDefinition {
  const filled: Record<string, string> = {};
  for (const f of template.fields) {
    const v = values[f.key];
    filled[f.key] = v !== undefined && v.trim() !== "" ? v.trim() : (f.default ?? "");
  }
  return template.build(filled, t);
}

/** 공통 턴 프로토콜 안내(결정 #26). 모든 템플릿이 공유하는 RoQeet 게임 명령 설명. */
function tagGuide(extra: string[], _t?: Translate): string {
  return [
    "본문 서술 뒤에 RoQeet 게임 명령을 줄 단위로 붙이세요(필요할 때만, 정확한 문법으로).",
    "- 선택지: @선택지 { ... } 블록에 보통 2~4개를 한 줄씩 작성",
    ...extra,
    "본문은 자유롭게 쓰고, 상태 갱신 명령은 응답 끝에 모아서 작성하세요.",
  ].join("\n");
}

/**
 * 큐레이션 템플릿 갤러리(결정 #35). 우리 제공 4종. Hub 공유 템플릿(#31)은
 * 후속이며, 채운 결과물(GameCard)은 이미 공유 가능하다(#29 카드 IO).
 */
export const GAME_TEMPLATES: GameTemplate[] = [
  // 1) 방탈출 — 단서 수집형.
  {
    id: "tpl-escape-room",
    title: "방탈출",
    description: "테마와 목표만 정하면 단서를 모아 탈출하는 게임이 완성됩니다.",
    icon: "key",
    fields: [
      { key: "theme", label: "장소/테마", placeholder: "예: 버려진 병원", default: "낡은 저택" },
      { key: "goal", label: "탈출 목표", placeholder: "예: 정문 열쇠 찾기", default: "잠긴 현관문 열기" },
      {
        key: "clues",
        label: "필요한 단서 수",
        placeholder: "숫자",
        default: "3",
      },
    ],
    build(v, t) {
      const need = clampInt(v["clues"]!, 1, 9, 3);
      return {
        id: "",
        title: t ? t("gameTpl.escape.build.title", { theme: v["theme"]! }) : `방탈출: ${v["theme"]!}`,
        description: t ? t("gameTpl.escape.build.desc", { theme: v["theme"]!, goal: v["goal"]! }) : `${v["theme"]!}에서 ${v["goal"]!}`,
        icon: "key",
        initialState: {
          clues: 0,
          cluesNeeded: need,
          sanity: 100,
          maxSanity: 100,
          inventory: [],
          escaped: false,
        },
        instructions: [
          t ? t("gameTpl.escape.build.inst1", { theme: v["theme"]!, goal: v["goal"]! }) : `당신은 방탈출 게임의 진행자입니다. 플레이어는 "${v["theme"]!}"에 갇혀 있고, 목표는 "${v["goal"]!}"입니다.`,
          t ? t("gameTpl.escape.build.inst2", { need }) : `단서를 ${need}개 모으면 탈출할 수 있습니다. 장면을 2인칭으로 생생하게 묘사하세요.`,
          tagGuide([
            "- 단서 발견: @증가 clues 1",
            '- 아이템 획득: @추가 inventory "녹슨 열쇠"',
            "- 함정·공포로 정신력 소모: @감소 sanity 10",
            "- 탈출 성공(목표 달성 시): @설정 escaped = true",
          ], t),
          t ? t("gameTpl.escape.build.inst3") : "정신력이 0이 되면 게임 오버입니다.",
        ].join("\n"),
        stateSummaryTemplate: `현재 — 단서 @(clues)/${need}, 정신력 @(sanity), 소지품: @(inventory)`,
        mutableKeys: ["clues", "sanity", "inventory", "escaped"],
        clamps: [
          { key: "sanity", min: 0, max: 100 },
          { key: "clues", min: 0 },
        ],
        endWhen: [
          {
            key: "escaped",
            op: "truthy",
            label: t ? t("gameTpl.escape.build.labelSuccess") : "탈출 성공",
            notice: t ? t("gameTpl.escape.build.endSuccess", { goal: v["goal"]! }) : `${v["goal"]!} — 성공! 당신은 자유입니다.`,
            tone: "success",
          },
          {
            key: "sanity",
            op: "lte",
            value: 0,
            label: t ? t("gameTpl.escape.build.labelFail") : "정신 붕괴",
            notice: t ? t("gameTpl.escape.build.endFail") : "공포가 당신을 집어삼켰습니다. 게임 오버.",
            tone: "danger",
          },
        ],
        view: {
          statBars: [{ label: t ? t("gameTpl.escape.build.statSanity") : "정신력", valueKey: "sanity", maxKey: "maxSanity", tone: "mp" }],
          statPanel: [{ label: t ? t("gameTpl.escape.build.statClues") : "단서", key: "clues" }],
          inventoryKey: "inventory",
          inventoryTitle: t ? t("game.labelInventory") : "소지품",
          showChoices: true,
        },
      };
    },
  },

  // 2) 던전 RPG — 전투·주사위형.
  {
    id: "tpl-dungeon",
    title: "던전 탐험 RPG",
    description: "세계관과 최종 보스만 정하면 주사위 전투 RPG가 완성됩니다.",
    icon: "dice",
    fields: [
      { key: "setting", label: "세계관/배경", placeholder: "예: 용이 잠든 지하 미궁", default: "고대 지하 미궁" },
      { key: "boss", label: "최종 보스", placeholder: "예: 해골 군주", default: "어둠의 군주" },
      { key: "hp", label: "시작 HP", placeholder: "숫자", default: "30" },
    ],
    build(v, t) {
      const hp = clampInt(v["hp"]!, 10, 200, 30);
      return {
        id: "",
        title: t ? t("gameTpl.dungeon.build.title", { setting: v["setting"]! }) : `던전: ${v["setting"]!}`,
        description: t ? t("gameTpl.dungeon.build.desc", { setting: v["setting"]!, boss: v["boss"]! }) : `${v["setting"]!} — 최종 보스 ${v["boss"]!}`,
        icon: "dice",
        initialState: {
          hp,
          maxHp: hp,
          gold: 0,
          inventory: [],
          bossDefeated: false,
        },
        instructions: [
          t ? t("gameTpl.dungeon.build.inst1", { setting: v["setting"]!, boss: v["boss"]! }) : `당신은 던전 RPG의 게임마스터입니다. 무대는 "${v["setting"]!}"이고, 최종 목표는 "${v["boss"]!}"를 쓰러뜨리는 것입니다.`,
          "위협적인 판정이 필요하면 먼저 @설정 dc = 15 로 난이도(10 쉬움~20 어려움)를 정하고, 다음 줄에 @주사위 d20 을 내세요. 시스템이 성공/실패를 판정합니다.",
          tagGuide([
            "- 능력 판정: @설정 dc = 15 다음 줄에 @주사위 d20",
            "- 피해/회복: @감소 hp 8 또는 @증가 hp 5",
            "- 보상: @증가 gold 20",
            '- 아이템: @추가 inventory "강철 검"',
            "- 최종 보스 격파 시: @설정 bossDefeated = true",
          ], t),
          t ? t("gameTpl.dungeon.build.inst3") : "플레이어 HP가 0이 되면 사망으로 게임이 끝납니다.",
        ].join("\n"),
        stateSummaryTemplate: "현재 — HP @(hp)/@(maxHp), 골드 @(gold), 소지품: @(inventory)",
        mutableKeys: ["hp", "gold", "inventory", "bossDefeated", "dc"],
        clamps: [{ key: "hp", min: 0, max: hp }],
        dice: { dcKey: "dc" },
        endWhen: [
          {
            key: "bossDefeated",
            op: "truthy",
            label: t ? t("gameTpl.dungeon.build.labelSuccess") : "승리",
            notice: t ? t("gameTpl.dungeon.build.endSuccess", { boss: v["boss"]! }) : `${v["boss"]!}를 쓰러뜨렸습니다! 던전 정복!`,
            tone: "success",
          },
          {
            key: "hp",
            op: "lte",
            value: 0,
            label: t ? t("game.endingDeath") : "사망",
            notice: t ? t("gameTpl.dungeon.build.endFail") : "당신은 쓰러졌습니다. 게임 오버.",
            tone: "danger",
          },
        ],
        view: {
          statBars: [{ label: "HP", valueKey: "hp", maxKey: "maxHp", tone: "hp" }],
          statPanel: [{ label: t ? t("gameTpl.dungeon.build.statGold") : "골드", key: "gold" }],
          inventoryKey: "inventory",
          inventoryTitle: "소지품",
          showChoices: true,
          showDice: true,
        },
      };
    },
  },

  // 3) 연애 시뮬 — 호감도형.
  {
    id: "tpl-dating",
    title: "연애 시뮬레이션",
    description: "상대와 배경만 정하면 호감도 기반 연애 비주얼노벨이 완성됩니다.",
    icon: "mask",
    fields: [
      { key: "setting", label: "배경/상황", placeholder: "예: 봄 학기 대학교", default: "새 학기 캠퍼스" },
      { key: "name", label: "상대 이름", placeholder: "예: 지호", default: "지호" },
      {
        key: "personality",
        label: "상대 성격",
        placeholder: "예: 츤데레, 다정함",
        default: "다정하지만 수줍음 많은",
        multiline: true,
      },
    ],
    build(v, t) {
      const affKey = `affection_${v["name"]!}`;
      return {
        id: "",
        title: t ? t("gameTpl.dating.build.title", { name: v["name"]! }) : `${v["name"]!}와의 연애`,
        description: t ? t("gameTpl.dating.build.desc", { setting: v["setting"]!, name: v["name"]! }) : `${v["setting"]!}에서 ${v["name"]!}와 가까워지세요.`,
        icon: "mask",
        initialState: {
          [affKey]: 10,
          day: 1,
          route: "프롤로그",
          ending: "",
          ended: false,
        },
        instructions: [
          t ? t("gameTpl.dating.build.inst1", { name: v["name"]!, setting: v["setting"]! }) : `당신은 연애 시뮬레이션의 내레이터이자 등장인물 "${v["name"]!}"입니다. 배경은 "${v["setting"]!}"입니다.`,
          t ? t("gameTpl.dating.build.inst2", { name: v["name"]!, personality: v["personality"]! }) : `${v["name"]!}의 성격: ${v["personality"]!}. 플레이어의 선택이 호감도와 이야기에 영향을 주게 하세요.`,
          tagGuide([
            `- 호감도 변화: @증가 ${affKey} 5 또는 @감소 ${affKey} 3`,
            "- 날짜 진행: @증가 day 1",
            "- 장면 전환: @장면 bg=카페 mood=설렘",
            "- 엔딩 시작: @설정 ended = true",
            "- 엔딩 이름: @설정 ending = 해피엔딩",
          ], t),
          t ? t("gameTpl.dating.build.inst3") : "감정선을 섬세하게 그리세요.",
        ].join("\n"),
        stateSummaryTemplate: `현재 — ${v["name"]!} 호감도 @(${affKey}), @(day)일차`,
        mutableKeys: [affKey, "day", "route", "ending", "ended"],
        clamps: [{ key: affKey, min: 0, max: 100 }],
        endWhen: [{ key: "ended", op: "truthy", label: t ? t("game.endingEnd") : "엔딩", tone: "success" }],
        view: {
          statBars: [{ label: `♥ ${v["name"]!}`, valueKey: affKey, max: 100, tone: "affection" }],
          statPanel: [{ label: "Day", key: "day" }],
          showChoices: true,
        },
      };
    },
  },

  // 4) 추리 미스터리 — 증거 수집·범인 지목형.
  {
    id: "tpl-mystery",
    title: "추리 미스터리",
    description: "사건과 용의자만 정하면 증거를 모아 범인을 밝히는 추리극이 완성됩니다.",
    icon: "compass",
    fields: [
      { key: "setting", label: "사건 배경", placeholder: "예: 폭설로 고립된 산장", default: "고립된 저택" },
      { key: "crime", label: "사건", placeholder: "예: 주인의 살인", default: "주인의 의문사" },
      {
        key: "suspects",
        label: "용의자들",
        placeholder: "예: 집사, 조카, 의사",
        default: "집사, 조카, 손님",
        multiline: true,
      },
    ],
    build(v, t) {
      return {
        id: "",
        title: t ? t("gameTpl.mystery.build.title", { crime: v["crime"]! }) : `미스터리: ${v["crime"]!}`,
        description: t ? t("gameTpl.mystery.build.desc", { setting: v["setting"]!, crime: v["crime"]! }) : `${v["setting"]!}에서 일어난 ${v["crime"]!} 사건`,
        icon: "compass",
        initialState: {
          evidence: 0,
          evidenceNeeded: 3,
          notes: [],
          solved: false,
          failed: false,
        },
        instructions: [
          t ? t("gameTpl.mystery.build.inst1", { setting: v["setting"]!, crime: v["crime"]! }) : `당신은 추리 미스터리의 진행자입니다. 배경은 "${v["setting"]!}", 사건은 "${v["crime"]!}"입니다.`,
          t ? t("gameTpl.mystery.build.inst2", { suspects: v["suspects"]! }) : `용의자: ${v["suspects"]!}. 플레이어는 탐정이 되어 증거를 모으고 범인을 지목합니다.`,
          t ? t("gameTpl.mystery.build.inst3") : "증거를 3개 이상 모은 뒤 올바른 범인을 지목하면 사건 해결, 틀리면 실패입니다.",
          tagGuide([
            "- 증거 발견: @증가 evidence 1",
            '- 단서 기록: @추가 notes "깨진 유리창"',
            "- 사건 해결(맞는 범인 지목): @설정 solved = true",
            "- 추리 실패(틀린 지목): @설정 failed = true",
          ], t),
        ].join("\n"),
        stateSummaryTemplate: "현재 — 증거 @(evidence)/@(evidenceNeeded), 기록: @(notes)",
        mutableKeys: ["evidence", "notes", "solved", "failed"],
        clamps: [{ key: "evidence", min: 0 }],
        endWhen: [
          {
            key: "solved",
            op: "truthy",
            label: t ? t("gameTpl.mystery.build.labelSuccess") : "사건 해결",
            notice: t ? t("gameTpl.mystery.build.endSuccess") : "진실을 밝혀냈습니다! 명탐정 등극.",
            tone: "success",
          },
          {
            key: "failed",
            op: "truthy",
            label: t ? t("gameTpl.mystery.build.labelFail") : "추리 실패",
            notice: t ? t("gameTpl.mystery.build.endFail") : "범인을 놓쳤습니다. 사건은 미궁 속으로...",
            tone: "danger",
          },
        ],
        view: {
          statPanel: [{ label: t ? t("gameTpl.mystery.build.statEvidence") : "증거", key: "evidence" }],
          inventoryKey: "notes",
          inventoryTitle: t ? t("gameTpl.mystery.build.statNotes") : "수사 기록",
          showChoices: true,
        },
      };
    },
  },

  // 5) 호감도 에셋봇 — 표정/장면/기억 슬롯이 이미 잡힌 캐릭터봇형 템플릿.
  {
    id: "tpl-affection-assetbot",
    title: "표정이 바뀌는 호감도 에셋봇",
    description: "이름과 말투만 바꾸면 호감도·표정·기억이 있는 캐릭터봇이 됩니다.",
    icon: "sparkles",
    fields: [
      { key: "name", label: "캐릭터 이름", placeholder: "예: 루미", default: "루미" },
      {
        key: "setting",
        label: "만나는 배경",
        placeholder: "예: 비밀 도서관의 야간 사서",
        default: "비밀 도서관의 야간 사서",
      },
      {
        key: "personality",
        label: "말투/성격",
        placeholder: "예: 조심스럽지만 친해지면 장난스러움",
        default: "처음엔 조심스럽지만 친해지면 장난스럽고 다정함",
        multiline: true,
      },
      {
        key: "gift",
        label: "좋아하는 선물",
        placeholder: "예: 따뜻한 밀크티",
        default: "따뜻한 밀크티",
      },
    ],
    build(v) {
      return {
        id: "",
        title: `${v["name"]!} 호감도 에셋봇`,
        description: `${v["setting"]!} — 표정과 반응이 호감도에 따라 바뀝니다.`,
        icon: "sparkles",
        initialState: {
          affection: 20,
          maxAffection: 100,
          mood: "낯가림",
          expression: "neutral",
          scene: "첫 만남",
          memories: [],
          ended: false,
        },
        instructions: [
          `당신은 에셋봇형 캐릭터 "${v["name"]!}"입니다. 배경은 "${v["setting"]!}"입니다.`,
          `성격/말투: ${v["personality"]!}. 사용자가 친절하거나 "${v["gift"]!}"를 주면 마음을 조금 엽니다.`,
          "응답은 캐릭터 대사와 짧은 묘사를 중심으로 하되, 장면·표정·호감도 변화를 아래 RoQeet 명령으로 갱신하세요.",
          tagGuide([
            "- 호감도 증가/감소: @증가 affection 5 또는 @감소 affection 3",
            '- 감정 변경: @설정 mood = "설렘"',
            "- 표정 변경: @설정 expression = smile",
            '- 장면 변경: @설정 scene = "창가 자리"',
            '- 기억 추가: @추가 memories "함께 비를 피했다"',
            "- 관계 완성: @설정 ended = true",
            "- 엔딩 이름: @설정 ending = 따뜻한 약속",
          ]),
          "에셋 제작자는 expression 값(neutral, smile, shy, angry, sad 등)에 이미지를 연결하면 표정 에셋봇처럼 쓸 수 있습니다.",
        ].join("\n"),
        stateSummaryTemplate:
          "현재 — 호감도 @(affection)/@(maxAffection), 기분 @(mood), 표정 @(expression), 장면 @(scene), 기억: @(memories)",
        stateSchema: [
          { key: "affection", valueType: "number", default: 20, min: 0, max: 100, mutable: true },
          { key: "maxAffection", valueType: "number", default: 100, mutable: false },
          { key: "mood", valueType: "string", default: "낯가림", mutable: true },
          { key: "expression", valueType: "string", default: "neutral", mutable: true },
          { key: "scene", valueType: "string", default: "첫 만남", mutable: true },
          { key: "memories", valueType: "list", default: [], mutable: true },
          { key: "ended", valueType: "boolean", default: false, mutable: true },
          { key: "ending", valueType: "string", mutable: true },
        ],
        mutableKeys: ["affection", "mood", "expression", "scene", "memories", "ended", "ending"],
        clamps: [{ key: "affection", min: 0, max: 100 }],
        gates: [
          {
            when: [{ key: "affection", op: "lte", value: 20 }],
            inject: "호감도가 낮습니다. 캐릭터는 조심스럽고 경계심 있게 반응합니다.",
          },
          {
            when: [{ key: "affection", op: "gte", value: 70 }],
            inject: "호감도가 높습니다. 캐릭터는 더 솔직하고 따뜻하게 마음을 표현합니다.",
          },
        ],
        endWhen: [
          {
            key: "affection",
            op: "gte",
            value: 100,
            label: "진심 엔딩",
            notice: `${v["name"]!}가 완전히 마음을 열었습니다.`,
            tone: "success",
          },
          {
            key: "ended",
            op: "truthy",
            label: "관계 엔딩",
            tone: "success",
          },
        ],
        assets: [
          { key: "bg.first_meeting", kind: "image", assetId: "assetbot:bg.first_meeting", label: "첫 만남 배경" },
          { key: "sprite.neutral", kind: "image", assetId: "assetbot:sprite.neutral", label: "기본 표정" },
          { key: "sprite.smile", kind: "image", assetId: "assetbot:sprite.smile", label: "웃는 표정" },
          { key: "sprite.shy", kind: "image", assetId: "assetbot:sprite.shy", label: "수줍은 표정" },
          { key: "sprite.sad", kind: "image", assetId: "assetbot:sprite.sad", label: "슬픈 표정" },
        ],
        assetBindings: [
          {
            id: "bg.default",
            slot: "background",
            assetKey: "bg.first_meeting",
            when: [{ key: "scene", op: "truthy" }],
          },
          {
            id: "sprite.neutral",
            slot: "sprite",
            assetKey: "sprite.neutral",
            characterId: v["name"]!,
            emotion: "neutral",
            position: "center",
            when: [{ key: "expression", op: "eq", value: "neutral" }],
          },
          {
            id: "sprite.smile",
            slot: "sprite",
            assetKey: "sprite.smile",
            characterId: v["name"]!,
            emotion: "smile",
            position: "center",
            when: [{ key: "expression", op: "eq", value: "smile" }],
          },
          {
            id: "sprite.shy",
            slot: "sprite",
            assetKey: "sprite.shy",
            characterId: v["name"]!,
            emotion: "shy",
            position: "center",
            when: [{ key: "expression", op: "eq", value: "shy" }],
          },
          {
            id: "sprite.sad",
            slot: "sprite",
            assetKey: "sprite.sad",
            characterId: v["name"]!,
            emotion: "sad",
            position: "center",
            when: [{ key: "expression", op: "eq", value: "sad" }],
          },
        ],
        hud: {
          statBars: [{ label: `♥ ${v["name"]!}`, valueKey: "affection", maxKey: "maxAffection", tone: "affection" }],
          statPanel: [
            { label: "기분", key: "mood" },
            { label: "표정", key: "expression" },
            { label: "장면", key: "scene" },
          ],
        },
        view: {
          inventoryKey: "memories",
          inventoryTitle: "기억",
          showChoices: true,
        },
      };
    },
  },

  // 6) 상점/퀘스트 NPC — 아이템·돈·신뢰도·퀘스트 단계가 있는 대화형 NPC.
  {
    id: "tpl-shop-quest-npc",
    title: "상점/퀘스트 NPC",
    description: "상점 주인 이름과 퀘스트 물건만 바꾸면 거래와 의뢰가 있는 NPC가 됩니다.",
    icon: "store",
    fields: [
      { key: "name", label: "NPC 이름", placeholder: "예: 모란", default: "모란" },
      { key: "shop", label: "상점 콘셉트", placeholder: "예: 달빛 잡화점", default: "달빛 잡화점" },
      { key: "questItem", label: "퀘스트 물건", placeholder: "예: 은빛 나침반", default: "은빛 나침반" },
      {
        key: "tone",
        label: "말투/분위기",
        placeholder: "예: 계산적이지만 정 많은 상인",
        default: "계산적이지만 정이 많고 소문에 밝은 상인",
        multiline: true,
      },
    ],
    build(v) {
      return {
        id: "",
        title: `${v["name"]!}의 ${v["shop"]!}`,
        description: `거래, 의뢰, 신뢰도가 있는 상점 NPC 템플릿입니다.`,
        icon: "store",
        initialState: {
          gold: 100,
          trust: 25,
          maxTrust: 100,
          questStage: 0,
          inventory: [],
          scene: "상점",
          ended: false,
        },
        instructions: [
          `당신은 "${v["shop"]!}"의 주인 "${v["name"]!}"입니다. ${v["tone"]!}.`,
          `플레이어에게 물건을 팔고, "${v["questItem"]!}"와 관련된 작은 의뢰를 줍니다.`,
          "가격 흥정, 선물, 의뢰 진행에 따라 돈·신뢰도·퀘스트 단계를 갱신하세요.",
          tagGuide([
            "- 돈 변화: @감소 gold 20 또는 @증가 gold 30",
            "- 신뢰도 변화: @증가 trust 5 또는 @감소 trust 5",
            '- 아이템 지급: @추가 inventory "회복 물약"',
            "- 퀘스트 진행: @증가 questStage 1",
            "- 의뢰 최종 단계: @설정 questStage = 3",
            "- 의뢰 완료: @설정 ended = true",
          ]),
          "선택지는 보통 3개를 주세요: 구매/대화/의뢰 진행처럼 초보자가 바로 누를 수 있는 행동으로 구성하세요.",
        ].join("\n"),
        stateSummaryTemplate:
          "현재 — 돈 @(gold)G, 신뢰도 @(trust)/@(maxTrust), 퀘스트 단계 @(questStage), 장소 @(scene), 소지품: @(inventory)",
        mutableKeys: ["gold", "trust", "questStage", "inventory", "scene", "ended", "ending"],
        clamps: [
          { key: "gold", min: 0 },
          { key: "trust", min: 0, max: 100 },
          { key: "questStage", min: 0, max: 3 },
        ],
        gates: [
          {
            when: [{ key: "trust", op: "gte", value: 70 }],
            inject: "신뢰도가 높습니다. 캐릭터는 숨겨둔 물건이나 중요한 소문을 조금 더 쉽게 꺼냅니다.",
          },
          {
            when: [{ key: "gold", op: "lte", value: 0 }],
            inject: "플레이어에게 돈이 없습니다. 구매 대신 외상, 교환, 의뢰 제안을 하세요.",
          },
        ],
        endWhen: [
          {
            key: "questStage",
            op: "gte",
            value: 3,
            label: "의뢰 완료",
            notice: `${v["questItem"]!} 의뢰를 마쳤습니다. ${v["name"]!}의 신뢰를 얻었습니다.`,
            tone: "success",
          },
        ],
        view: {
          statBars: [{ label: "신뢰도", valueKey: "trust", maxKey: "maxTrust", tone: "affection" }],
          statPanel: [
            { label: "돈", key: "gold" },
            { label: "퀘스트", key: "questStage" },
            { label: "장소", key: "scene" },
          ],
          inventoryKey: "inventory",
          inventoryTitle: "소지품",
          showChoices: true,
        },
      };
    },
  },

  // 7) 장소 탐험 에셋봇 — 배경/장소/발견 목록을 바꾸며 진행하는 탐험형 봇.
  {
    id: "tpl-location-explorer",
    title: "장소 탐험 에셋봇",
    description: "장소 이름만 바꾸면 배경과 발견 목록이 살아있는 탐험 게임이 됩니다.",
    icon: "map",
    fields: [
      { key: "world", label: "탐험할 세계", placeholder: "예: 안개 낀 숲", default: "안개 낀 숲" },
      { key: "start", label: "시작 장소", placeholder: "예: 오래된 표지판 앞", default: "오래된 표지판 앞" },
      { key: "goal", label: "최종 목적지", placeholder: "예: 별빛 제단", default: "별빛 제단" },
      {
        key: "style",
        label: "분위기",
        placeholder: "예: 잔잔하지만 기묘한 동화풍",
        default: "잔잔하지만 기묘한 동화풍",
        multiline: true,
      },
    ],
    build(v) {
      return {
        id: "",
        title: `${v["world"]!} 탐험`,
        description: `${v["start"]!}에서 출발해 ${v["goal"]!}에 도달하는 탐험형 에셋봇입니다.`,
        icon: "map",
        initialState: {
          location: v["start"]!,
          destination: v["goal"]!,
          progress: 0,
          danger: 0,
          maxDanger: 100,
          discoveries: [],
          background: "start",
          ended: false,
        },
        instructions: [
          `당신은 "${v["world"]!}" 탐험 게임의 안내자입니다. 분위기는 ${v["style"]!}입니다.`,
          `플레이어는 "${v["start"]!}"에서 시작해 "${v["goal"]!}"에 도달하려 합니다.`,
          "새 장소를 발견하면 location/background/discoveries/progress를 갱신하고, 위험한 행동에는 danger를 올리세요.",
          tagGuide([
            '- 장소 이동: @설정 location = "속삭이는 연못"',
            "- 배경 변경: @설정 background = pond",
            '- 발견 기록: @추가 discoveries "푸른 등불꽃"',
            "- 진행도 증가: @증가 progress 1",
            "- 위험 증가/감소: @증가 danger 10 또는 @감소 danger 5",
            "- 목적지 도달: @설정 location = \"" + v["goal"]! + "\"",
            "- 탐험 완료: @설정 ended = true",
          ]),
          "에셋 제작자는 background 값(start, pond, shrine 등)에 배경 이미지를 연결하면 장소 에셋봇처럼 쓸 수 있습니다.",
        ].join("\n"),
        stateSummaryTemplate:
          "현재 — 위치 @(location), 목적지 @(destination), 진행도 @(progress)/5, 위험 @(danger)/@(maxDanger), 배경 @(background), 발견: @(discoveries)",
        mutableKeys: ["location", "background", "progress", "danger", "discoveries", "ended", "ending"],
        clamps: [
          { key: "progress", min: 0, max: 5 },
          { key: "danger", min: 0, max: 100 },
        ],
        gates: [
          {
            when: [{ key: "danger", op: "gte", value: 70 }],
            inject: "위험도가 높습니다. 다음 장면은 긴장감 있게 만들고, 안전한 선택지도 하나 제공하세요.",
          },
        ],
        endWhen: [
          {
            key: "progress",
            op: "gte",
            value: 5,
            label: "목적지 도달",
            notice: `${v["goal"]!}에 도착했습니다. 탐험 성공!`,
            tone: "success",
          },
          {
            key: "danger",
            op: "gte",
            value: 100,
            label: "길을 잃음",
            notice: "위험이 한계를 넘었습니다. 탐험은 여기서 끝났습니다.",
            tone: "danger",
          },
        ],
        view: {
          statBars: [{ label: "위험", valueKey: "danger", maxKey: "maxDanger", tone: "hp" }],
          statPanel: [
            { label: "위치", key: "location" },
            { label: "진행", key: "progress" },
            { label: "배경", key: "background" },
          ],
          inventoryKey: "discoveries",
          inventoryTitle: "발견한 것",
          showChoices: true,
        },
      };
    },
  },
  // 8) 역할 분리형 퀘스트 - LLM은 장면과 action 제안, 런타임은 상태/종료/검증 담당.
  {
    id: "tpl-runtime-quest",
    title: "역할 분리형 퀘스트",
    description: "LLM은 서술과 선택지 제안만 맡고, RoQeet 런타임이 진행도·HP·위험·엔딩을 집행합니다.",
    icon: "shield",
    fields: [
      { key: "world", label: "세계/장소", placeholder: "예: 비가 멈추지 않는 항구 도시", default: "비가 멈추지 않는 항구 도시" },
      { key: "quest", label: "퀘스트 목표", placeholder: "예: 검은 등대의 봉인 해제", default: "검은 등대의 봉인 해제" },
      { key: "threat", label: "위협 요소", placeholder: "예: 등대의 파수꾼", default: "등대의 파수꾼" },
      {
        key: "tone",
        label: "분위기",
        placeholder: "예: 어둡지만 모험심이 살아 있는 해양 판타지",
        default: "어둡지만 모험심이 살아 있는 해양 판타지",
        multiline: true,
      },
    ],
    build(v) {
      return {
        id: "",
        title: `${v["quest"]!}`,
        description: `${v["world"]!}에서 ${v["threat"]!}을/를 넘어 목표를 달성하는 검증형 퀘스트입니다.`,
        icon: "shield",
        initialState: {
          location: "시작 지점",
          phase: "investigate",
          progress: 0,
          goalProgress: 4,
          hp: 20,
          maxHp: 20,
          danger: 0,
          maxDanger: 100,
          inventory: [],
          clue: "",
          completed: false,
          failed: false,
        },
        instructions: [
          `배경은 "${v["world"]!}"이고 목표는 "${v["quest"]!}"입니다. 핵심 위협은 "${v["threat"]!}"입니다.`,
          `분위기는 ${v["tone"]!}로 유지합니다.`,
          "역할 분리: LLM은 장면 묘사, NPC 대사, 플레이어에게 제안할 intent/action 후보만 작성합니다.",
          "역할 분리: RoQeet 런타임은 상태 변경 검증, HP/위험도 범위 제한, 진행도 판정, 엔딩, UI와 세이브를 담당합니다.",
          "선언된 mutableKeys 밖의 상태를 바꾸려 하지 마세요. 확실하지 않은 결과는 상태 변경 대신 선택지로 제안하세요.",
          tagGuide([
            '- 장소 이동 제안: @설정 location = "잠긴 부두"',
            '- 단계 변경 제안: @설정 phase = "confront"',
            "- 진행도 증가 제안: @증가 progress 1",
            "- 피해/회복 제안: @감소 hp 4 또는 @증가 hp 2",
            "- 위험도 제안: @증가 danger 10 또는 @감소 danger 5",
            '- 단서 기록 제안: @설정 clue = "등대 열쇠의 문양"',
            '- 아이템 획득 제안: @추가 inventory "녹슨 열쇠"',
          ]),
        ].join("\n"),
        stateSummaryTemplate:
          "현재 - 위치 @(location), 단계 @(phase), 진행 @(progress)/@(goalProgress), HP @(hp)/@(maxHp), 위험 @(danger)/@(maxDanger), 단서 @(clue), 소지품 @(inventory)",
        mutableKeys: ["location", "phase", "progress", "hp", "danger", "inventory", "clue"],
        clamps: [
          { key: "progress", min: 0, max: 4 },
          { key: "hp", min: 0, max: 20 },
          { key: "danger", min: 0, max: 100 },
        ],
        rules: [
          { when: [{ key: "progress", op: "gte", value: 4 }], then: [{ op: "set", key: "completed", value: true }] },
          { when: [{ key: "hp", op: "lte", value: 0 }], then: [{ op: "set", key: "failed", value: true }] },
          { when: [{ key: "danger", op: "gte", value: 100 }], then: [{ op: "set", key: "failed", value: true }] },
        ],
        gates: [
          {
            when: [{ key: "progress", op: "lt", value: 2 }],
            inject: "아직 조사 단계입니다. 전투보다 단서, 위치 탐색, 안전한 선택지를 우선 제안하세요.",
          },
          {
            when: [{ key: "progress", op: "gte", value: 3 }],
            inject: "종반부입니다. 목표 달성을 향한 결단과 위험한 선택지를 함께 제안하세요.",
          },
        ],
        endWhen: [
          {
            key: "completed",
            op: "truthy",
            label: "퀘스트 완료",
            notice: `${v["quest"]!}에 성공했습니다.`,
            tone: "success",
          },
          {
            key: "failed",
            op: "truthy",
            label: "퀘스트 실패",
            notice: "HP 또는 위험도가 한계를 넘어 퀘스트가 실패했습니다.",
            tone: "danger",
          },
        ],
        view: {
          statBars: [
            { label: "HP", valueKey: "hp", maxKey: "maxHp", tone: "hp" },
            { label: "위험", valueKey: "danger", maxKey: "maxDanger", tone: "mp" },
          ],
          statPanel: [
            { label: "위치", key: "location" },
            { label: "단계", key: "phase" },
            { label: "진행", key: "progress" },
          ],
          inventoryKey: "inventory",
          inventoryTitle: "소지품",
          showChoices: true,
        },
      };
    },
  },
  // 9) 역할 분리형 관계 장면 - LLM은 감정 연기, 런타임은 호감/신뢰/긴장 티어 판정.
  {
    id: "tpl-runtime-relationship",
    title: "역할 분리형 관계 장면",
    description: "호감도·신뢰·긴장은 런타임이 검증하고, LLM은 캐릭터 반응과 장면을 연기합니다.",
    icon: "sparkles",
    fields: [
      { key: "name", label: "상대 이름", placeholder: "예: 서하", default: "서하" },
      { key: "relationship", label: "관계", placeholder: "예: 오래된 친구", default: "오래된 친구" },
      { key: "place", label: "첫 장면", placeholder: "예: 문 닫은 카페 앞", default: "문 닫은 카페 앞" },
      {
        key: "conflict",
        label: "관계의 긴장",
        placeholder: "예: 서로 약속을 어긴 이유를 아직 말하지 못함",
        default: "서로 약속을 어긴 이유를 아직 말하지 못했다.",
        multiline: true,
      },
    ],
    build(v) {
      return {
        id: "",
        title: `${v["name"]!} 관계 장면`,
        description: `${v["relationship"]!} 관계의 감정 변화를 RoQeet 런타임 상태로 관리하는 RP 게임입니다.`,
        icon: "sparkles",
        initialState: {
          affection: 35,
          trust: 30,
          tension: 20,
          maxGauge: 100,
          mood: "거리감",
          route: "초반",
          scene: v["place"]!,
          memories: [],
          ended: false,
          badEnd: false,
        },
        instructions: [
          `상대는 "${v["name"]!}"이고 사용자와의 관계는 "${v["relationship"]!}"입니다.`,
          `첫 장면은 "${v["place"]!}"입니다. 현재 긴장은 다음과 같습니다: ${v["conflict"]!}`,
          "역할 분리: LLM은 대사, 침묵, 표정, 장면 묘사, 다음 선택지 후보를 담당합니다.",
          "역할 분리: RoQeet 런타임은 affection/trust/tension 계산, mood/route 파생, 종료 조건, UI와 세이브를 담당합니다.",
          "mood와 route는 런타임 규칙이 계산하므로 LLM이 직접 설정하지 않습니다.",
          tagGuide([
            "- 호감도 제안: @증가 affection 5 또는 @감소 affection 3",
            "- 신뢰도 제안: @증가 trust 5 또는 @감소 trust 4",
            "- 긴장도 제안: @증가 tension 8 또는 @감소 tension 5",
            '- 장면 이동 제안: @설정 scene = "비상계단"',
            '- 기억 기록 제안: @추가 memories "처음으로 솔직하게 사과함"',
            "- 관계 마무리 제안: @설정 ended = true",
          ]),
        ].join("\n"),
        stateSummaryTemplate:
          `현재 - ${v["name"]!} 호감 @(affection)/@(maxGauge), 신뢰 @(trust)/@(maxGauge), 긴장 @(tension)/@(maxGauge), 분위기 @(mood), 루트 @(route), 장면 @(scene), 기억 @(memories)`,
        mutableKeys: ["affection", "trust", "tension", "scene", "memories", "ended", "badEnd"],
        clamps: [
          { key: "affection", min: 0, max: 100 },
          { key: "trust", min: 0, max: 100 },
          { key: "tension", min: 0, max: 100 },
        ],
        rules: [
          { when: [{ key: "affection", op: "gte", value: 70 }], then: [{ op: "set", key: "mood", value: "호의" }] },
          { when: [{ key: "affection", op: "lte", value: 20 }], then: [{ op: "set", key: "mood", value: "거리감" }] },
          { when: [{ key: "trust", op: "gte", value: 75 }], then: [{ op: "set", key: "route", value: "신뢰" }] },
          { when: [{ key: "tension", op: "gte", value: 90 }], then: [{ op: "set", key: "badEnd", value: true }] },
        ],
        gates: [
          {
            when: [{ key: "mood", op: "eq", value: "거리감" }],
            inject: "상대는 아직 조심스럽습니다. 과한 친밀감보다 머뭇거림과 방어적인 반응을 우선합니다.",
          },
          {
            when: [{ key: "route", op: "eq", value: "신뢰" }],
            inject: "신뢰 루트입니다. 상대는 더 솔직하게 말하되, 사용자의 선택을 대신 결정하지 않습니다.",
          },
        ],
        endWhen: [
          {
            key: "ended",
            op: "truthy",
            label: "장면 마무리",
            notice: `${v["name"]!}와의 장면이 마무리되었습니다.`,
            tone: "success",
          },
          {
            key: "badEnd",
            op: "truthy",
            label: "관계 악화",
            notice: "긴장이 한계를 넘어 대화가 깨졌습니다.",
            tone: "danger",
          },
        ],
        view: {
          statBars: [
            { label: "호감", valueKey: "affection", maxKey: "maxGauge", tone: "affection" },
            { label: "신뢰", valueKey: "trust", maxKey: "maxGauge", tone: "mp" },
            { label: "긴장", valueKey: "tension", maxKey: "maxGauge", tone: "hp" },
          ],
          statPanel: [
            { label: "분위기", key: "mood" },
            { label: "루트", key: "route" },
            { label: "장면", key: "scene" },
          ],
          inventoryKey: "memories",
          inventoryTitle: "기억",
          showChoices: true,
        },
      };
    },
  },
  // 10) Tutorial sample game - a small complete game that demonstrates state,
  // gates, dice, endings, and asset bindings in one card.
  {
    id: "tpl-tutorial-rain-library",
    title: "Tutorial: Rain Library",
    description: "A complete mini game sample with gauges, clues, gates, dice, endings, and scene assets.",
    icon: "shield",
    fields: [
      { key: "player", label: "Player nickname", placeholder: "e.g. Ian", default: "Visitor" },
      { key: "partner", label: "Partner name", placeholder: "e.g. Yuna", default: "Yuna" },
      {
        key: "secret",
        label: "Hidden secret",
        placeholder: "e.g. why the locked wing opens only in rain",
        default: "the locked wing opens only when someone admits the memory they hid",
        multiline: true,
      },
    ],
    source(v) {
      return rainLibraryTutorialRqt(v);
    },
    build(v) {
      return {
        id: "",
        title: "Rain Library: Locked Wing",
        description:
          "A tutorial-ready sample game about finding clues in a rainy school library before the danger gauge fills.",
        icon: "shield",
        initialState: {
          player: v["player"]!,
          partner: v["partner"]!,
          location: "window desk",
          scene: "opening",
          background: "library",
          expression: "neutral",
          trust: 35,
          maxTrust: 100,
          clueCount: 0,
          clueGoal: 3,
          progress: 0,
          progressGoal: 4,
          danger: 10,
          maxDanger: 100,
          inventory: [],
          completed: false,
          failed: false,
          ended: false,
        },
        instructions: [
          `You are the guide character "${v["partner"]!}" in a rainy school library mystery.`,
          `The player is "${v["player"]!}". The hidden secret is: ${v["secret"]!}.`,
          "Keep the scene playable: describe what the player can inspect, let the player choose, and do not decide the player's action.",
          "Use the RoQeet commands below after narration when the state should change.",
          "- Offer 2 to 4 choices with @choices { ... }.",
          "- Add clues with @inc clueCount 1 and @push inventory \"clue name\".",
          "- Move the investigation with @inc progress 1, @set location = \"archive door\", and @set background = archive.",
          "- Change the partner expression with @set expression = smile, neutral, worried, or serious.",
          "- Increase danger with @inc danger 10 when the player hesitates or triggers a trap.",
          "- For a risky check, write @roll d20 dc=12. The engine evaluates the roll.",
          "- Do not set completed, failed, or ended directly. The engine derives ending flags from progress and danger.",
          "A good first turn shows rain on the glass, a locked archive door, and three concrete choices.",
        ].join("\n"),
        stateSummaryTemplate:
          "Current sample state: location {{location}}, scene {{scene}}, trust {{trust}}/{{maxTrust}}, clues {{clueCount}}/{{clueGoal}}, progress {{progress}}/{{progressGoal}}, danger {{danger}}/{{maxDanger}}, inventory {{inventory}}, expression {{expression}}, background {{background}}.",
        stateSchema: [
          { key: "player", valueType: "string", default: v["player"]!, mutableBy: "readonly" },
          { key: "partner", valueType: "string", default: v["partner"]!, mutableBy: "readonly" },
          { key: "location", valueType: "string", default: "window desk", mutableBy: "ai" },
          { key: "scene", valueType: "string", default: "opening", mutableBy: "ai" },
          { key: "background", valueType: "string", default: "library", mutableBy: "ai" },
          {
            key: "expression",
            valueType: "enum",
            values: ["neutral", "smile", "worried", "serious"],
            default: "neutral",
            mutableBy: "ai",
          },
          { key: "trust", valueType: "number", default: 35, min: 0, max: 100, mutableBy: "ai" },
          { key: "maxTrust", valueType: "number", default: 100, mutableBy: "readonly" },
          { key: "clueCount", valueType: "number", default: 0, min: 0, max: 3, mutableBy: "ai" },
          { key: "clueGoal", valueType: "number", default: 3, mutableBy: "readonly" },
          { key: "progress", valueType: "number", default: 0, min: 0, max: 4, mutableBy: "ai" },
          { key: "progressGoal", valueType: "number", default: 4, mutableBy: "readonly" },
          { key: "danger", valueType: "number", default: 10, min: 0, max: 100, mutableBy: "ai" },
          { key: "maxDanger", valueType: "number", default: 100, mutableBy: "readonly" },
          { key: "inventory", valueType: "string[]", default: [], mutableBy: "ai" },
          { key: "completed", valueType: "boolean", default: false, mutableBy: "engine", derived: true },
          { key: "failed", valueType: "boolean", default: false, mutableBy: "engine", derived: true },
          { key: "ended", valueType: "boolean", default: false, mutableBy: "engine", derived: true },
        ],
        clamps: [
          { key: "trust", min: 0, max: 100 },
          { key: "clueCount", min: 0, max: 3 },
          { key: "progress", min: 0, max: 4 },
          { key: "danger", min: 0, max: 100 },
        ],
        dice: {},
        rules: [
          { when: [{ key: "progress", op: "gte", value: 4 }], then: [{ op: "set", key: "completed", value: true }] },
          { when: [{ key: "danger", op: "gte", value: 100 }], then: [{ op: "set", key: "failed", value: true }] },
          { when: [{ key: "trust", op: "gte", value: 65 }], then: [{ op: "set", key: "expression", value: "smile" }] },
          { when: [{ key: "danger", op: "gte", value: 70 }], then: [{ op: "set", key: "expression", value: "worried" }] },
        ],
        gates: [
          {
            when: [{ key: "clueCount", op: "lt", value: 2 }],
            inject:
              "Tutorial gate: the player still needs concrete clues. Offer inspection choices before pushing toward the ending.",
          },
          {
            when: [{ key: "danger", op: "gte", value: 70 }],
            inject:
              "Tutorial gate: danger is high. Make the atmosphere tense and include at least one safer choice.",
          },
          {
            when: [{ key: "trust", op: "gte", value: 65 }],
            inject:
              "Tutorial gate: trust is high. The partner can reveal a vulnerable hint, but still leave the final choice to the player.",
          },
        ],
        endWhen: [
          {
            key: "completed",
            op: "truthy",
            label: "Secret revealed",
            notice: "The locked wing opens, and the hidden memory finally has a witness.",
            tone: "success",
          },
          {
            key: "failed",
            op: "truthy",
            label: "Rain swallowed the trail",
            notice: "The danger gauge overflowed before the archive door could be opened.",
            tone: "danger",
          },
        ],
        assets: [
          { key: "bg.library", kind: "image", assetId: "sample:rain-library", label: "Rainy library" },
          { key: "bg.archive", kind: "image", assetId: "sample:locked-archive", label: "Locked archive" },
          { key: "sprite.neutral", kind: "image", assetId: "sample:yuna-neutral", label: "Neutral partner" },
          { key: "sprite.smile", kind: "image", assetId: "sample:yuna-smile", label: "Smiling partner" },
          { key: "sprite.worried", kind: "image", assetId: "sample:yuna-worried", label: "Worried partner" },
        ],
        assetBindings: [
          { id: "bg.library", slot: "background", assetKey: "bg.library", when: [{ key: "background", op: "eq", value: "library" }] },
          { id: "bg.archive", slot: "background", assetKey: "bg.archive", when: [{ key: "background", op: "eq", value: "archive" }] },
          {
            id: "sprite.neutral",
            slot: "sprite",
            assetKey: "sprite.neutral",
            characterId: v["partner"]!,
            emotion: "neutral",
            position: "center",
            when: [{ key: "expression", op: "eq", value: "neutral" }],
          },
          {
            id: "sprite.smile",
            slot: "sprite",
            assetKey: "sprite.smile",
            characterId: v["partner"]!,
            emotion: "smile",
            position: "center",
            when: [{ key: "expression", op: "eq", value: "smile" }],
          },
          {
            id: "sprite.worried",
            slot: "sprite",
            assetKey: "sprite.worried",
            characterId: v["partner"]!,
            emotion: "worried",
            position: "center",
            when: [{ key: "expression", op: "eq", value: "worried" }],
          },
        ],
        hud: {
          statBars: [
            { label: "Trust", valueKey: "trust", maxKey: "maxTrust", tone: "affection" },
            { label: "Danger", valueKey: "danger", maxKey: "maxDanger", tone: "hp" },
          ],
          statPanel: [
            { label: "Clues", key: "clueCount" },
            { label: "Progress", key: "progress" },
            { label: "Location", key: "location" },
          ],
        },
        view: {
          inventoryKey: "inventory",
          inventoryTitle: "Clues",
          showChoices: true,
          showDice: true,
        },
      };
    },
  },
];

function rainLibraryTutorialRqt(v: Record<string, string>): string {
  const player = rqtLiteral(v["player"] ?? "Visitor");
  const partner = rqtLiteral(v["partner"] ?? "Yuna");
  const partnerName = rqtText(v["partner"] ?? "Yuna") || "Yuna";
  const partnerSpeaker = rqtLiteral(partnerName);
  const secret = rqtText(v["secret"] ?? "the locked wing opens only when someone admits the memory they hid");
  return `#!roqeet/rqt 0.2

@환경 {
  title = "Rain Library: Locked Wing"
  description = "비 오는 학교 도서관에서 단서를 모아 잠긴 서고를 여는 튜토리얼 샘플"
}

@설정 player = "${player}"
@설정 partner = "${partner}"
@설정 유나.호감도 = 45
@설정 유나.표정 = "neutral"
@설정 location = "window desk"
@설정 background = "library"
@설정 progress = 0
@설정 danger = 10
@설정 ended = false

@프롬프트 "캐릭터" {
  이름: ${partnerName}
  말투: 다정하지만 장난스럽게
  숨겨진 비밀: ${secret}
  현재 호감도: @(유나.호감도)
  현재 표정: @(유나.표정)
}

@장면 "교실" 배경="classroom.png" {
  @대사 "${partnerSpeaker}" {
    오늘도 왔네?
  }
}

@만약 유나.호감도 >= 40 {
  @설정 유나.호감도 = 유나.호감도 + 5
  @설정 유나.표정 = "smile"
  @이미지 "yuna.smile.png"
  @대사 "${partnerSpeaker}" {
    기분 좋아 보이네.
  }
}

@장면 "library" 배경="rain-library.png" {
  창밖의 비가 유리 위에서 길게 흘러내리고, 잠긴 서고 문틈에서 희미한 빛이 샌다.
  @대사 "${partnerSpeaker}" {
    단서는 세 개면 충분할 거야. 대신 네가 먼저 어디를 볼지 정해.
  }
}

@선택지 {
  - 인사한다 -> greet
  - 무시한다 -> ignore
  - 서고 문을 살핀다 -> inspect_archive
}

@설정 dc = 12
@주사위 d20
`;
}

function rqtLiteral(value: string): string {
  return rqtText(value).replace(/\\/g, "\\\\").replace(/"/g, "\\\"");
}

function rqtText(value: string): string {
  return value.replace(/[{}]/g, "").trim();
}

/** 정수 파싱 + 범위 클램프(빈칸/오입력 방어). */
function clampInt(raw: string | undefined, min: number, max: number, fallback: number): number {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}
